import { useState } from "react";
import { CalendarClock, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { TodayBudgetConfirm } from "./TodayBudgetConfirm";
import { BudgetChangesList } from "./BudgetChangesList";
import { money, resetLabel } from "../lib/budgetTime";

// Owner control for TODAY's budget: whole dollars between the env daily budget and $50, confirmed in a dialog.
export const TodayBudgetCard = ({ gate, viewer, changes, onSet }) => {
  const [value, setValue] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const lo = gate?.override_min_usd ?? 1;
  const hi = gate?.override_max_usd ?? 50;
  const allowed = !!viewer?.can_raise_budget;

  const review = (e) => {
    e.preventDefault();
    const n = Number(value);
    if (!value || !Number.isInteger(n)) return setError("Enter a whole-dollar amount.");
    if (n < lo || n > hi) return setError(`Choose between $${lo} and $${hi}.`);
    setError("");
    setPending(n);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await onSet(pending);
      setValue("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not change today's budget. Please retry.");
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  return (
    <section data-testid="today-budget-card" className="mt-8 rounded-2xl border border-border bg-card p-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <p className="font-heading font-bold">Today's daily budget</p>
        <span data-testid="today-budget-current" className="font-mono2 text-sm font-semibold">
          {money(gate?.budget_usd)}{gate?.override ? ` · set by ${gate.override.by}` : " · from env"}
        </span>
      </div>
      <p data-testid="today-budget-reset" className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
        <CalendarClock className="h-3.5 w-3.5" /> Valid for today only, {resetLabel(gate?.resets_at)}. Range ${lo}–${hi}, $1 steps.
      </p>
      <form onSubmit={review} className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="today-budget" className="block text-xs uppercase tracking-widest text-muted-foreground font-mono2">
            New budget for today (USD)
          </label>
          <input
            id="today-budget"
            data-testid="today-budget-input"
            type="number"
            step={1}
            min={lo}
            max={hi}
            value={value}
            disabled={!allowed || busy}
            onChange={(e) => setValue(e.target.value)}
            placeholder={String(Math.min(hi, Math.max(lo, Math.ceil(gate?.budget_usd || lo))))}
            className="mt-1 w-40 rounded-full border border-border bg-background px-4 h-10 text-sm outline-none focus:ring-2 ring-accent/40 disabled:opacity-50"
          />
        </div>
        <Button type="submit" data-testid="today-budget-submit" className="rounded-full h-10 px-5 font-bold gap-2" disabled={!allowed || busy || !value}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Set today's budget
        </Button>
      </form>
      {!allowed && <p data-testid="today-budget-not-owner" className="text-xs text-amber-800 mt-2">{viewer?.why || "Checking your account…"}</p>}
      {error && <p data-testid="today-budget-error" className="text-xs text-rose-700 mt-2">{error}</p>}
      <TodayBudgetConfirm gate={gate} amount={pending} busy={busy} onCancel={() => setPending(null)} onConfirm={confirm} />
      <BudgetChangesList changes={changes} />
    </section>
  );
};
